import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { analysisService } from '../../services/analysisService';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { SectionType } from '../../types';

// Icon mapping for section types
const sectionIconMap: Record<SectionType, string> = {
  HEADING_1: '📄',
  HEADING_2: '📋',
  HEADING_3: '📝',
  PARAGRAPH: '📃',
  LIST_ITEM: '📌',
  TABLE: '📊',
  QUOTE: '💬',
};

// Section type Spanish labels
const sectionLabelMap: Record<SectionType, string> = {
  HEADING_1: 'Título Principal',
  HEADING_2: 'Subtítulo',
  HEADING_3: 'Encabezado',
  PARAGRAPH: 'Párrafo',
  LIST_ITEM: 'Elemento de Lista',
  TABLE: 'Tabla',
  QUOTE: 'Cita',
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['sections', 'history', page],
    queryFn: () => analysisService.getAnalysisHistory({ page, limit }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-white">Historial de Análisis</h1>
            <p className="text-white/90">
              {data?.pagination.total || 0} secciones analizadas
            </p>
          </div>
          <Button
            onClick={() => navigate('/analysis/text')}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            Nuevo Análisis
          </Button>
        </div>
      </div>

      {/* Sections List */}
      {data?.sections && data.sections.length > 0 ? (
        <div className="space-y-3">
          {data.sections.map((section) => (
            <Card
              key={section.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/section/${section.id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Section Title and Type */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{sectionIconMap[section.sectionType]}</span>
                      <h3 className="font-bold text-lg text-foreground">
                        {section.title || `${sectionLabelMap[section.sectionType]}`}
                      </h3>
                      <Badge variant="outline" className="ml-2">
                        {sectionLabelMap[section.sectionType]}
                      </Badge>
                    </div>

                    {/* Document/Analysis Info */}
                    <p className="text-sm text-muted-foreground mb-2">
                      {section.analysis.sourceType === 'MANUAL' ? '✍️ ' : ''}
                      {section.analysis.sourceType === 'PDF' ? '📄 ' : ''}
                      {section.analysis.sourceType === 'DOCX' ? '📝 ' : ''}
                      {section.analysis.sourceType === 'URL' ? '🌐 ' : ''}
                      {section.analysis.title}
                    </p>

                    {/* Content Preview */}
                    <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                      {section.content.substring(0, 200)}...
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(section.analysis.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {section.totalWords} palabras
                      </span>
                      {section.fleschScore != null && (
                        <Badge variant={getScoreBadgeVariant(Number(section.fleschScore))}>
                          Flesch: {Number(section.fleschScore).toFixed(1)}
                        </Badge>
                      )}
                      {section.analysis.sector && (
                        <Badge variant="secondary">
                          {section.analysis.sector.icon} {section.analysis.sector.name}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/section/${section.id}`);
                    }}
                  >
                    Ver
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-muted-foreground mb-4">No hay secciones analizadas en el historial</p>
            <Button onClick={() => navigate('/analysis/text')}>
              Crear primer análisis
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            variant="outline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </Button>
          <span className="px-4 py-2 text-sm text-muted-foreground">
            Página {page} de {data.pagination.pages}
          </span>
          <Button
            onClick={() => setPage(page + 1)}
            disabled={page >= data.pagination.pages}
            variant="outline"
          >
            Siguiente
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      )}
    </div>
  );
}

function getScoreBadgeVariant(score: number): 'success' | 'warning' | 'default' {
  if (score >= 70) return 'success';
  if (score >= 50) return 'warning';
  return 'default';
}
